import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetResourceEventsInput,
  ResourceEventWithNavigationPropertiesDto,
} from '../../proxy/resource-events/models';
import { ResourceEventService } from '../../proxy/resource-events/resource-event.service';

@Component({
  selector: 'app-resource-event',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './resource-event.component.html',
  styles: [],
})
export class ResourceEventComponent implements OnInit {
  data: PagedResultDto<ResourceEventWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetResourceEventsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: ResourceEventWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: ResourceEventService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<ResourceEventWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetResourceEventsInput;
  }

  buildForm() {
    const {
      eventDate,
      resourceId,
      eventTypeId,
      aUserId,
    } = this.selected?.resourceEvent || {};

    this.form = this.fb.group({
      eventDate: [eventDate ? new Date(eventDate) : null, [Validators.required]],
      resourceId: [resourceId ?? null, []],
      eventTypeId: [eventTypeId ?? null, []],
      aUserId: [aUserId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.resourceEvent.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: ResourceEventWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: ResourceEventWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Analysis::DeleteConfirmationMessage',
      'Analysis::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.resourceEvent.id)),
    ).subscribe(this.list.get);
  }
}
